import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { cancelClassEnrollment, enrollInClass } from "@/http/coursesApi";
import { getCookie } from "@/utils/cookies";
import {
  getAppointmentsQueryKey,
  getClientCoursesQueryKey,
  getCoursesQueryKey,
  getTicketsQueryKey,
} from "@/utils/queryKeys";
import { composeRequestKey } from "@/utils/composeRequestKey";

interface EnrollClassParams {
  appointmentId: string;
  ticketId: string | null;
}

interface CancelClassParams {
  appointmentId: string;
}

const invalidateAll = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({ queryKey: getCoursesQueryKey() });
  queryClient.invalidateQueries({ queryKey: getClientCoursesQueryKey() });
  queryClient.invalidateQueries({ queryKey: composeRequestKey({ name: "course_classes" }) });
  // Страница «Ваши занятия» берёт данные отсюда — без этого запись/отмена появятся
  // только после ручной перезагрузки.
  queryClient.invalidateQueries({ queryKey: getAppointmentsQueryKey() });
  // После записи списываются занятия с абонемента — обновляем остаток.
  queryClient.invalidateQueries({ queryKey: getTicketsQueryKey() });
};

export const useEnrollClassMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ appointmentId, ticketId }: EnrollClassParams) => {
      const userToken = getCookie("user_token");
      if (!userToken) {
        throw new Error("Необходимо авторизоваться");
      }
      const response = await enrollInClass(appointmentId, ticketId, userToken);
      if (!response.data.result) {
        throw new Error(response.data.error_message || "Не удалось записаться на занятие");
      }
      return response.data;
    },
    onSuccess: () => {
      invalidateAll(queryClient);
      toast.success("Вы записаны на занятие");
    },
    onError: (error: Error) => {
      toast.error("Ошибка записи", { description: error.message });
    },
  });
};

export const useCancelClassMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ appointmentId }: CancelClassParams) => {
      const userToken = getCookie("user_token");
      if (!userToken) {
        throw new Error("Необходимо авторизоваться");
      }
      const response = await cancelClassEnrollment(appointmentId, userToken);
      if (!response.data.result) {
        throw new Error(response.data.error_message || "Не удалось отменить запись");
      }
      return response.data;
    },
    onSuccess: () => {
      invalidateAll(queryClient);
      toast.success("Запись отменена");
    },
    onError: (error: Error) => {
      toast.error("Ошибка отмены", { description: error.message });
    },
  });
};
